import { Grid } from '../../core/Grid.js';
import { Position, Direction, oppositeDir, isInside, positionKey } from '../../core/GridGeometry.js';
import { GridRenderer, BadgeQuadrant } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS, colorWithAlpha } from '../../rendering/Renderer.js';
import { isVortexGame } from '../IVortexGame.js';
import { TurnsGame, TurnType, CartPath } from './TurnsGame.js';
import { TweenScheduler, MOTION, staggerDelay } from '../../rendering/juice.js';

export interface TurnsRenderConfig extends Partial<RenderConfig> {
    laneColor?: string;
    unusedTurnColor?: string;
    laneWidth?: number;
}

const TURNS_DEFAULTS = {
    laneColor: COLORS.teal,
    unusedTurnColor: COLORS.amber,
    laneWidth: 2.5,
};

type Vec = { x: number; y: number };

/** Unit step vectors; screen y grows downward. */
const DIR_VEC: Record<Direction, Vec> = {
    E: { x: 1, y: 0 },
    W: { x: -1, y: 0 },
    N: { x: 0, y: -1 },
    S: { x: 0, y: 1 },
};

/** Unit vector to the right of travel (the driving side for right-hand traffic). */
const RIGHT_OF: Record<Direction, Vec> = {
    E: { x: 0, y: 1 },
    S: { x: -1, y: 0 },
    W: { x: 0, y: -1 },
    N: { x: 1, y: 0 },
};

function dist(p: Vec, q: Vec): number {
    return Math.hypot(q.x - p.x, q.y - p.y);
}

/** Rough length of a quadratic through `ctrl`: the mean of its control polygon and its
 *  chord. Only used to pace a dash, where a few percent either way doesn't show. */
function quadLength(a: Vec, ctrl: Vec, b: Vec): number {
    return (dist(a, ctrl) + dist(ctrl, b) + dist(a, b)) / 2;
}

/** Intersection of the tangent lines A+t·u and B+s·v (the corner a 90° road arc bends
 *  around); falls back to the midpoint if the lines are parallel. */
function tangentIntersect(A: Vec, u: Vec, B: Vec, v: Vec): Vec {
    const det = -u.x * v.y + v.x * u.y;
    if (Math.abs(det) < 1e-6) return { x: (A.x + B.x) / 2, y: (A.y + B.y) / 2 };
    const t = ((B.x - A.x) * -v.y - -v.x * (B.y - A.y)) / det;
    return { x: A.x + u.x * t, y: A.y + u.y * t };
}

interface ClearAnalysis {
    /** Step at which the first out-of-order clear happens; the lanes go amber from here. */
    offenseStep: number | null;
    /** One badge per digit crossing: which digit, the running crossing count, the pass's
     *  direction (the badge sits on that lane's side), and its colour (teal/red/amber). */
    badges: Array<{ digit: number; value: number; dir: Direction; color: string }>;
    /** Digits crossed more than twice — drawn as a red square, no badges. */
    redSquares: Set<number>;
}

// ── Turn animation (docs/juice.md) ────────────────────────────────────
//
// A turn arrow is a circular arrow, and both of its states are the *same* arrow:
// ↺ is ↻ seen in a mirror, because the gap the arrowhead sits in is centred at
// the top of both. So placing sweeps the arc round from its tail to its head —
// drawing the glyph the way a hand would — and cycling mirrors it end for end,
// the same verb as the Orbits centre icon, which is the same glyph.

interface TurnTween {
    kind: 'enter' | 'change' | 'exit';
    /** Clock time the tween begins — in the future while a stagger delay runs. */
    start: number;
    /** The arrow being drawn; for a cycle, the one it is turning *away* from. */
    type: TurnType;
    /** Exit only: the colour it had on the last frame it existed. */
    color: string;
}

// Changing a turn re-routes the whole drive, and the rest of the board settles
// instantly as simulation should. The clicked cell is the exception: its own lane
// pieces re-draw themselves along the way the cart now takes them, which is the
// answer to the question the click asked — *what does this do to the cart here?*
// It stays one cell, so it reads as feedback for the click rather than the board
// replaying itself.

export class TurnsRenderer extends GridRenderer {
    private turnsConfig: typeof TURNS_DEFAULTS;
    private gateMode = false;
    private driveOnRight = true;   // cosmetic lane-side swap (right-hand traffic by default)

    // Animation state, all renderer-local (docs/juice.md)
    private tweens = new TweenScheduler();
    private turnTweens = new Map<string, TurnTween>();
    private turnState = new Map<string, TurnType>();   // what the last frame drew
    private turnColors = new Map<string, string>();    // last colour, for removals
    private turnsSynced = false;                       // first frame seeds, never animates
    private lanePlay: { key: string; start: number } | null = null;
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating = false;
    private lastGame: TurnsGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: TurnsRenderConfig = {}) {
        super(canvas, grid, config);
        this.turnsConfig = { ...TURNS_DEFAULTS, ...config };
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every turn arrow at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.turnTweens.clear();
        this.lanePlay = null;
    }

    /** Swap which side of the road the cart drives on (purely cosmetic). */
    setDriveOnRight(enabled: boolean): void {
        this.driveOnRight = enabled;
    }

    render(game: TurnsGame): void {
        const grid = game.getGrid();
        const path = game.getPath();
        const analysis = this.analyzeClears(path);

        this.lastGame = game;
        this.animating = false;
        // Before the lanes: the diff is what starts a cell's lane replay, and the
        // lanes are drawn first.
        this.syncTurnTweens(game.getTurns());

        this.clear();
        this.drawGrid();

        // A turn placed on the start corner makes it an ordinary turn cell, not the clean
        // E-out/W-in terminal — so skip the terminal/rim special-casing there.
        const startHasTurn = game.getTurnAt(TurnsGame.START) !== null;

        this.drawLanes(path, analysis, startHasTurn);
        if (isVortexGame(game)) this.drawActiveVortexGates(game);
        this.drawTurns(game, path);
        this.drawDigits(grid);
        this.drawDigitMarks(analysis);

        // Path highlight, drawn over the turn arrows.
        const selDigit = game.getSelectedDigit();
        if (game.isStartSelected()) this.drawPathHighlight(path, i => i === 0);
        else if (selDigit !== null) this.drawPathHighlight(path, i => grid.getDigitAt(path.steps[i].pos) === selDigit);

        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }

        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    // ── Clear-order analysis ───────────────────────────────────────

    /**
     * Walk the drive, counting each digit crossing (a bounce counts twice). A digit cleared
     * on its second crossing gets a badge showing the running crossing count (so with N
     * digits badges run up to 2N). Clears must happen in order 1…N: badges are teal while in
     * order, red on the first out-of-order clear, amber after — and the lanes turn amber from
     * that step on. A digit crossed more than twice is an error: a red square, no badge.
     */
    private analyzeClears(path: CartPath): ClearAnalysis {
        const grid = this.grid;
        const steps = path.steps;
        const n = steps.length;

        // Every crossing in path order. A bounce crosses twice in one step — inbound along the
        // arrival heading, outbound along the departure — so its two badges sit on both lanes.
        let hits = 0;
        const cross = new Map<number, number>();
        const crossings: Array<{ digit: number; value: number; dir: Direction; cc: number; step: number }> = [];
        for (let i = 0; i < n; i++) {
            const d = grid.getDigitAt(steps[i].pos);
            if (d === null) continue;
            const inDir = steps[(i - 1 + n) % n].dir;
            const dirs: Direction[] = steps[i].uturn ? [inDir, steps[i].dir] : [steps[i].dir];
            for (const dir of dirs) {
                hits++;
                const cc = (cross.get(d) ?? 0) + 1;
                cross.set(d, cc);
                crossings.push({ digit: d, value: hits, dir, cc, step: i });
            }
        }

        const redSquares = new Set<number>();
        for (const [d, cc] of cross) if (cc > 2) redSquares.add(d);

        // The clears (second crossings) must run 1…N; find the first out-of-order one.
        let expected = grid.minDigit;
        let offenseValue: number | null = null;
        let offenseStep: number | null = null;
        for (const c of crossings) {
            if (redSquares.has(c.digit) || c.cc !== 2 || offenseValue !== null) continue;
            if (c.digit === expected) expected++;
            else { offenseValue = c.value; offenseStep = c.step; }
        }

        // One badge per crossing: teal before the offending clear, red on it, amber after.
        const badges: ClearAnalysis['badges'] = [];
        for (const c of crossings) {
            if (redSquares.has(c.digit)) continue;
            const color = offenseValue === null || c.value < offenseValue ? COLORS.teal
                : c.value === offenseValue ? COLORS.red
                    : COLORS.amber;
            badges.push({ digit: c.digit, value: c.value, dir: c.dir, color });
        }

        return { offenseStep, badges, redSquares };
    }

    // ── Lanes ──────────────────────────────────────────────────────

    /** Perpendicular lane offset in pixels for a step traveling `dir` (driving side). */
    private laneOffset(dir: Direction): Vec {
        const gap = this.config.cellSize * 0.15 * (this.driveOnRight ? 1 : -1);
        const r = RIGHT_OF[dir];
        return { x: r.x * gap, y: r.y * gap };
    }

    /**
     * Draw the drive as curved lanes. Each cell hosts one road piece from the entry edge
     * (in its lane) to the exit edge: a straight for a pass-through, an arc through the
     * tangent corner for a 90° turn (tight for a right turn, wide crossing for a left one),
     * and a rim-touching bulge for a wall U-turn. Digit cells are gapped and marked with
     * entry/exit arrows instead.
     */
    private drawLanes(path: CartPath, analysis: ClearAnalysis, startHasTurn: boolean): void {
        const steps = path.steps;
        const n = steps.length;
        if (n === 0) return;

        const s = this.config.cellSize;
        const h = s / 2;
        const now = this.tweens.now();

        this.ctx.lineWidth = this.turnsConfig.laneWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        for (let i = 0; i < n; i++) {
            const step = steps[i];
            const inDir = steps[(i - 1 + n) % n].dir;   // arrival heading (loop is cyclic)
            const outDir = step.dir;
            const pos = step.pos;
            const p = this.lanePlayAt(pos, now);   // 1 unless this cell was just clicked

            // The lane runs teal until an out-of-order clear, amber from that step on.
            const color = analysis.offenseStep !== null && i >= analysis.offenseStep
                ? COLORS.amber
                : this.turnsConfig.laneColor;
            this.ctx.strokeStyle = color;

            const C = this.getCellCenter(pos);
            const inV = DIR_VEC[inDir];
            const outV = DIR_VEC[outDir];
            const entry = { x: C.x - inV.x * h + this.laneOffset(inDir).x, y: C.y - inV.y * h + this.laneOffset(inDir).y };
            const exit = { x: C.x + outV.x * h + this.laneOffset(outDir).x, y: C.y + outV.y * h + this.laneOffset(outDir).y };
            const isUTurn = outDir === oppositeDir(inDir);

            // The start cell's *seam* — step 0, always the start corner (traceCart begins
            // there). Only the seam is a terminal: the cart begins here heading E and the
            // loop closes back into it. Its in/out arrows off the rim mark the corner, the
            // outbound lane is teal (the drive's start) and the inbound is the end (amber
            // once out of order). Without a turn the terminal is two straight lanes; with
            // one, the departure comes in from the rim heading east and the turn bends it
            // out, and the return runs back out to the rim.
            //
            // Every *other* visit to the corner — the cart driving back through it, or
            // bouncing off the top edge there — is an ordinary cell and falls through to
            // the normal cases below, U-turn bulge included.
            if (i === 0) {
                const endColor = analysis.offenseStep !== null ? COLORS.amber : this.turnsConfig.laneColor;
                this.drawStartArrows(this.turnsConfig.laneColor, endColor);
                if (!startHasTurn) {
                    this.drawStartCellLanes(pos, inDir, outDir, this.turnsConfig.laneColor, endColor, p);
                } else {
                    this.strokeStartEntry(pos, outDir, color, p);       // departure: rim → outDir
                    this.strokeStartReturn(pos, inDir, endColor, p);    // return: inDir → rim
                }
                continue;
            }

            // A Vortex jump. The drive skips the gate the cart *enters* — it steps straight
            // from the cell before it to the far gate — so neither cell can be drawn as an
            // ordinary through-cell: the entry gate would get no lane at all, and this one
            // would get a full lane reaching in from its far edge, as if the cart came from
            // behind the gate. Draw the two halves that physically exist instead: in to the
            // entry gate, and out of this one.
            if (step.teleport) {
                const prev = steps[(i - 1 + n) % n];
                const entryGate = this.grid.move(prev.pos, prev.dir);
                // Each half belongs to its own cell, so each plays on its own cell's cue.
                if (isInside(entryGate)) {
                    this.strokeGateHalf(entryGate, prev.dir, true, color, this.lanePlayAt(entryGate, now));
                }
                this.strokeGateHalf(pos, outDir, false, color, p);
                continue;
            }

            if (this.grid.hasDigitAt(pos)) {
                // Gap the lane over the digit and mark the pass with in/out arrows on each lane.
                // A bounce still shows its semicircle — it sits at the rim, clear of the digit.
                // (No turn can sit on a digit, so these never replay.)
                this.drawLaneArrow(pos, inDir, true, color);
                this.drawLaneArrow(pos, outDir, false, color);
                if (isUTurn) this.strokeUTurn(entry, exit, inV, false);
                continue;
            }

            if (isUTurn) {
                this.strokeUTurn(entry, exit, inV, true, p);
                continue;
            }

            this.ctx.beginPath();
            this.ctx.moveTo(entry.x, entry.y);
            let len: number;
            if (inDir === outDir) {
                this.ctx.lineTo(exit.x, exit.y);
                len = dist(entry, exit);
            } else {
                // 90° turn: arc through the corner where the two lanes' tangents meet.
                const ctrl = tangentIntersect(entry, inV, exit, outV);
                this.ctx.quadraticCurveTo(ctrl.x, ctrl.y, exit.x, exit.y);
                len = quadLength(entry, ctrl, exit);
            }
            this.strokeLane(len, p);
        }
    }

    /**
     * A wall U-turn: two straight lanes to the rim joined by a small semicircle (diameter =
     * the lane spacing) at the tip. `includeStraights` off draws just the tip semicircle —
     * used on a digit cell, where the straights would cross the digit but the tip does not.
     */
    private strokeUTurn(entry: Vec, exit: Vec, inV: Vec, includeStraights: boolean, p: number = 1): void {
        const s = this.config.cellSize;
        const gapMag = s * 0.15;
        // Two straights out to the rim and back, joined by a semicircle of radius `gapMag`.
        const len = (includeStraights ? 2 * (s - gapMag) : 0) + Math.PI * gapMag;

        this.ctx.beginPath();
        if (includeStraights) this.ctx.moveTo(entry.x, entry.y);
        this.appendUTurn(entry, exit, inV, includeStraights);
        this.strokeLane(len, p);
    }

    /** Append the U-turn geometry to the current path. With `straights`, assumes the current
     *  point is `entry` and adds the two lanes; without, just the tip semicircle. */
    private appendUTurn(entry: Vec, exit: Vec, inV: Vec, straights: boolean): void {
        const s = this.config.cellSize;
        const gapMag = s * 0.15;
        const L = s - gapMag;   // straight length so the tip semicircle just touches the rim
        const tIn = { x: entry.x + inV.x * L, y: entry.y + inV.y * L };
        const tOut = { x: exit.x + inV.x * L, y: exit.y + inV.y * L };
        const ctr = { x: (tIn.x + tOut.x) / 2, y: (tIn.y + tOut.y) / 2 };
        const aIn = Math.atan2(tIn.y - ctr.y, tIn.x - ctr.x);
        const aOut = Math.atan2(tOut.y - ctr.y, tOut.x - ctr.x);
        const aRim = Math.atan2(inV.y, inV.x);   // the tip bulges toward the rim (+inV)
        const twoPi = Math.PI * 2;
        const norm = (a: number) => ((a % twoPi) + twoPi) % twoPi;
        const rimGoingUp = norm(aRim - aIn) < norm(aOut - aIn);

        if (straights) this.ctx.lineTo(tIn.x, tIn.y);
        else this.ctx.moveTo(tIn.x, tIn.y);
        this.ctx.arc(ctr.x, ctr.y, gapMag, aIn, aOut, !rimGoingUp);
        if (straights) this.ctx.lineTo(exit.x, exit.y);
    }

    /**
     * Half a lane in a gate cell: the rim-to-centre half the cart drives before it vanishes
     * (`inbound`), or the centre-to-rim half it drives after emerging. Stopping at the centre
     * is what makes the jump read — the lane meets the gate marker instead of crossing it.
     */
    private strokeGateHalf(
        pos: Position, dir: Direction, inbound: boolean, color: string, p: number = 1
    ): void {
        const C = this.getCellCenter(pos);
        const h = this.config.cellSize / 2;
        const v = DIR_VEC[dir];
        const off = this.laneOffset(dir);
        const rim = { x: C.x + (inbound ? -1 : 1) * v.x * h + off.x, y: C.y + (inbound ? -1 : 1) * v.y * h + off.y };
        const mid = { x: C.x + off.x, y: C.y + off.y };

        this.ctx.strokeStyle = color;
        this.ctx.beginPath();
        // Built the way the cart drives it — in to the gate, or out from it — so a
        // replay sweeps the right way. Identical at rest, either order.
        const [a, b] = inbound ? [rim, mid] : [mid, rim];
        this.ctx.moveTo(a.x, a.y);
        this.ctx.lineTo(b.x, b.y);
        this.strokeLane(h, p);
    }

    /** An edge triangle nudged onto its travel lane, so the two directions don't overlap. */
    private drawLaneArrow(pos: Position, travelDir: Direction, inbound: boolean, color: string): void {
        const off = this.laneOffset(travelDir);
        this.ctx.save();
        this.ctx.translate(off.x, off.y);
        this.drawEdgeTriangle(pos, travelDir, color, inbound);
        this.ctx.restore();
    }

    /** The two straight terminal lanes across the start cell (no-turn case). */
    private drawStartCellLanes(
        pos: Position, inDir: Direction, outDir: Direction,
        outColor: string, inColor: string, p: number = 1
    ): void {
        const C = this.getCellCenter(pos);
        const h = this.config.cellSize / 2;
        const inV = DIR_VEC[inDir];
        const outV = DIR_VEC[outDir];
        const inOff = this.laneOffset(inDir);
        const outOff = this.laneOffset(outDir);

        const outRim = { x: C.x - outV.x * h + outOff.x, y: C.y - outV.y * h + outOff.y };
        const outEnd = { x: C.x + outV.x * h + outOff.x, y: C.y + outV.y * h + outOff.y };
        const inEnd = { x: C.x - inV.x * h + inOff.x, y: C.y - inV.y * h + inOff.y };
        const inRim = { x: C.x + inV.x * h + inOff.x, y: C.y + inV.y * h + inOff.y };

        this.ctx.strokeStyle = outColor;
        this.ctx.beginPath();
        this.ctx.moveTo(outRim.x, outRim.y);
        this.ctx.lineTo(outEnd.x, outEnd.y);
        this.strokeLane(dist(outRim, outEnd), p);

        this.ctx.strokeStyle = inColor;
        this.ctx.beginPath();
        this.ctx.moveTo(inEnd.x, inEnd.y);
        this.ctx.lineTo(inRim.x, inRim.y);
        this.strokeLane(dist(inEnd, inRim), p);
    }

    /** The start's initial departure when a turn sits on the corner: it enters from the west
     *  rim heading east and the turn bends it out to `outDir`. */
    private strokeStartEntry(pos: Position, outDir: Direction, color: string, p: number = 1): void {
        const C = this.getCellCenter(pos);
        const h = this.config.cellSize / 2;
        const inDir = TurnsGame.START_DIR;   // east — from the rim
        const inV = DIR_VEC[inDir];
        const outV = DIR_VEC[outDir];
        const entry = { x: C.x - inV.x * h + this.laneOffset(inDir).x, y: C.y - inV.y * h + this.laneOffset(inDir).y };
        const exit = { x: C.x + outV.x * h + this.laneOffset(outDir).x, y: C.y + outV.y * h + this.laneOffset(outDir).y };

        this.ctx.strokeStyle = color;
        this.ctx.beginPath();
        this.ctx.moveTo(entry.x, entry.y);
        let len: number;
        if (outDir === inDir) {
            this.ctx.lineTo(exit.x, exit.y);
            len = dist(entry, exit);
        } else {
            const ctrl = tangentIntersect(entry, inV, exit, outV);
            this.ctx.quadraticCurveTo(ctrl.x, ctrl.y, exit.x, exit.y);
            len = quadLength(entry, ctrl, exit);
        }
        this.strokeLane(len, p);
    }

    /** The start's return when a turn sits on the corner: the cart arrives heading `inDir` and
     *  runs back out to the west rim heading west. */
    private strokeStartReturn(pos: Position, inDir: Direction, color: string, p: number = 1): void {
        const C = this.getCellCenter(pos);
        const h = this.config.cellSize / 2;
        const outDir = oppositeDir(TurnsGame.START_DIR);   // west — out to the rim
        const inV = DIR_VEC[inDir];
        const outV = DIR_VEC[outDir];
        const entry = { x: C.x - inV.x * h + this.laneOffset(inDir).x, y: C.y - inV.y * h + this.laneOffset(inDir).y };
        const exit = { x: C.x + outV.x * h + this.laneOffset(outDir).x, y: C.y + outV.y * h + this.laneOffset(outDir).y };

        this.ctx.strokeStyle = color;
        this.ctx.beginPath();
        this.ctx.moveTo(entry.x, entry.y);
        let len: number;
        if (inDir === outDir) {
            this.ctx.lineTo(exit.x, exit.y);
            len = dist(entry, exit);
        } else {
            const ctrl = tangentIntersect(entry, inV, exit, outV);
            this.ctx.quadraticCurveTo(ctrl.x, ctrl.y, exit.x, exit.y);
            len = quadLength(entry, ctrl, exit);
        }
        this.strokeLane(len, p);
    }

    /**
     * The start terminal's in/out arrows, just outside the west rim — always shown, even when
     * a turn sits on the corner. They mark the cart's start (it begins heading east) and its
     * return, using the fixed start heading rather than the (turn-bent) actual directions.
     */
    private drawStartArrows(outColor: string, inColor: string): void {
        const pos = TurnsGame.START;
        const outDir = TurnsGame.START_DIR;      // east — into the grid
        const inDir = oppositeDir(outDir);       // west — back out at the rim
        const inV = DIR_VEC[inDir];
        const outward = this.config.cellSize * 0.3;

        for (const [dir, inbound, col] of [[outDir, true, outColor], [inDir, false, inColor]] as const) {
            const off = this.laneOffset(dir);
            this.ctx.save();
            this.ctx.translate(inV.x * outward + off.x, inV.y * outward + off.y);
            this.drawEdgeTriangle(pos, dir, col, inbound);
            this.ctx.restore();
        }
    }

    // ── Turns ──────────────────────────────────────────────────────

    /** Scoring overlay: redraw the turns (primary) in amber. */
    protected drawScoringPrimary(game: TurnsGame): void {
        this.drawTurns(game, game.getPath(), COLORS.amber);
    }

    private drawTurns(game: TurnsGame, path: CartPath, colorOverride?: string): void {
        const driven = new Set<string>();
        for (const step of path.steps) driven.add(`${step.pos.row},${step.pos.col}`);

        const turns = game.getTurns();
        // The scoring overlay redraws the turns in a flat colour on a frozen board:
        // it reads state, it doesn't advance it. (The diff itself already ran, at
        // the top of render.)
        const live = colorOverride === undefined;
        const now = this.tweens.now();

        for (const [key, type] of turns) {
            const [row, col] = key.split(',').map(Number);
            const color = colorOverride ??
                (driven.has(key) ? this.turnsConfig.laneColor : this.turnsConfig.unusedTurnColor);
            if (live) this.turnColors.set(key, color);

            const tween = live ? this.turnTweens.get(key) : undefined;
            const shape = tween ? this.turnShape(tween, now) : { type, sweep: 1, flip: 1, done: true };
            if (tween && shape.done) this.turnTweens.delete(key);
            else if (tween) this.animating = true;

            this.drawTurnArrow({ row, col }, shape.type, color, shape.sweep, shape.flip);
        }

        // Turns being removed are already gone from game state — they only exist
        // here, un-drawing in the colour they wore when they went.
        for (const [key, tween] of live ? this.turnTweens : []) {
            if (tween.kind !== 'exit' || turns.has(key)) continue;
            const [row, col] = key.split(',').map(Number);
            const shape = this.turnShape(tween, now);
            if (shape.done) {
                this.turnTweens.delete(key);
                this.turnColors.delete(key);
                continue;
            }
            this.animating = true;
            this.drawTurnArrow({ row, col }, shape.type, tween.color, shape.sweep, shape.flip);
        }
    }

    /**
     * Diff the board against the last frame and start a tween per changed cell:
     * placed arrows sweep themselves round, a cycled one mirrors, a removed one
     * un-sweeps. A bulk change (Reset, Restore best) fans out along the cart's
     * heading out of the start corner rather than popping at once.
     */
    private syncTurnTweens(turns: Map<string, TurnType>): void {
        if (!this.tweens.isEnabled()) {
            this.turnTweens.clear();
            this.turnState = new Map(turns);
            return;
        }
        // First frame on this board: adopt it as-is, or opening a grid with a
        // saved solution would draw the whole thing on.
        if (!this.turnsSynced) {
            this.turnState = new Map(turns);
            this.turnsSynced = true;
            return;
        }

        const changed: Array<{ key: string; prev?: TurnType; next?: TurnType }> = [];
        for (const [key, next] of turns) {
            const prev = this.turnState.get(key);
            if (prev !== next) changed.push({ key, prev, next });
        }
        for (const [key, prev] of this.turnState) {
            if (!turns.has(key)) changed.push({ key, prev });
        }
        if (changed.length === 0) return;

        // Fan out from the top-left start corner, the way the cart sets off.
        const rank = (key: string) => {
            const [row, col] = key.split(',').map(Number);
            return (row + col) * this.grid.size + row;
        };
        changed.sort((a, b) => rank(a.key) - rank(b.key));

        const now = this.tweens.now();
        // One cell changing is a click, and its lanes replay to answer it. A bulk
        // change (Reset, Restore best) isn't a question about any one cell, and a
        // board of lanes all redrawing at once would be noise.
        this.lanePlay = changed.length === 1 ? { key: changed[0].key, start: now } : null;

        changed.forEach(({ key, prev, next }, i) => {
            const start = now + staggerDelay(i, changed.length);
            if (next === undefined) {
                this.turnTweens.set(key, {
                    kind: 'exit', start, type: prev!,
                    color: this.turnColors.get(key) ?? this.turnsConfig.unusedTurnColor
                });
            } else if (prev === undefined) {
                this.turnTweens.set(key, { kind: 'enter', start, type: next, color: '' });
            } else {
                // ↻ and ↺ are the same arrow mirrored, so a cycle turns it over.
                this.turnTweens.set(key, { kind: 'change', start, type: prev, color: '' });
            }
        });

        this.turnState = new Map(turns);
    }

    /**
     * How much of `pos`'s lane pieces to draw this frame — 1 unless that cell was
     * just clicked and is replaying the cart's way through it.
     */
    private lanePlayAt(pos: Position, now: number): number {
        if (!this.lanePlay || this.lanePlay.key !== positionKey(pos)) return 1;
        const { value, done } = this.tweens.tween(this.lanePlay.start, MOTION.replay, now);
        if (done) {
            this.lanePlay = null;
            return 1;
        }
        this.animating = true;
        return value;
    }

    /**
     * Stroke the path just built, or its leading `p`. Every lane piece is built in
     * travel order, so a dash long enough to be the whole piece — shown from its
     * start — draws the lane on the way the cart drives it, curves included.
     * `len` only paces the dash, so a rough length is plenty.
     */
    private strokeLane(len: number, p: number): void {
        if (p >= 1) { this.ctx.stroke(); return; }
        if (p <= 0 || len <= 0) return;
        this.ctx.save();
        this.ctx.setLineDash([len * p, len * 2]);
        this.ctx.stroke();
        this.ctx.restore();
    }

    /** How much of the arc is drawn this frame, and which way round it faces. */
    private turnShape(
        tween: TurnTween, now: number
    ): { type: TurnType; sweep: number; flip: number; done: boolean } {
        const { value, done } = this.tweens.tween(tween.start, MOTION[tween.kind], now);
        switch (tween.kind) {
            case 'enter':
                return { type: tween.type, sweep: value, flip: 1, done };
            case 'exit':
                // The role's curve runs the *disappearance*, so invert it to get
                // what's left on the board.
                return { type: tween.type, sweep: 1 - value, flip: 1, done };
            case 'change':
                // +1 → -1: the arrow turns edge-on and comes back mirrored.
                return { type: tween.type, sweep: 1, flip: 1 - 2 * value, done };
        }
    }

    /** A circular arrow (↻ / ↺) centered in the cell — same visual family as Orbits. A solid
     *  background-colored disc sits behind it so the arrow reads cleanly over the lanes.
     *  `sweep` is how much of the arc is drawn, from its tail round to the arrowhead;
     *  `flip` mirrors it horizontally, which is exactly what turns ↻ into ↺. */
    private drawTurnArrow(
        pos: Position, type: TurnType, color: string, sweep: number = 1, flip: number = 1
    ): void {
        if (sweep <= 0.01) return;
        const center = this.getCellCenter(pos);
        const s = this.config.cellSize;
        const r = s * 0.19;
        const cw = type === 'CW';

        // Solid disc underlay — a touch larger for the "crossing" (left) turn, whose arc and
        // head sit wider: CCW with right-hand lanes, CW with left-hand. Sized from the arrow
        // as it currently reads, so a mirrored one gets the mirrored arrow's disc. It stays
        // a circle through a flip (a squashed mask would show the lanes through its edges)
        // and fades with the sweep, so it never punches a hole before the arrow is there.
        const shown: TurnType = flip >= 0 ? type : (type === 'CW' ? 'CCW' : 'CW');
        const leftTurn = this.driveOnRight ? shown === 'CCW' : shown === 'CW';
        this.ctx.save();
        this.ctx.globalAlpha = sweep;
        this.ctx.fillStyle = COLORS.background;
        this.ctx.beginPath();
        this.ctx.arc(center.x, center.y, r + s * (leftTurn ? 0.11 : 0.1), 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.restore();

        if (Math.abs(flip) < 0.02) return;   // edge-on mid-flip: only the disc shows

        this.ctx.save();
        this.ctx.translate(center.x, center.y);
        this.ctx.scale(flip, 1);

        // ~270° arc leaving a gap for the arrowhead, drawn only as far as `sweep`.
        const gap = Math.PI * 0.5;
        const a0 = -Math.PI / 2 + (cw ? gap / 2 : -gap / 2);
        const a1 = a0 + (cw ? 1 : -1) * (Math.PI * 2 - gap) * sweep;
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = 2.5;
        this.ctx.lineCap = 'round';
        this.ctx.beginPath();
        this.ctx.arc(0, 0, r, a0, a1, !cw);
        this.ctx.stroke();

        // Arrowhead at the arc's end, tangent to the circle in the sweep direction —
        // it leads the stroke round as the arrow draws itself.
        const headLen = s * 0.1 * sweep;
        const headX = Math.cos(a1) * r;
        const headY = Math.sin(a1) * r;
        const tangent = a1 + (cw ? Math.PI / 2 : -Math.PI / 2);
        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.ctx.moveTo(headX + Math.cos(tangent) * headLen, headY + Math.sin(tangent) * headLen);
        this.ctx.lineTo(headX + Math.cos(tangent + 2.4) * headLen, headY + Math.sin(tangent + 2.4) * headLen);
        this.ctx.lineTo(headX + Math.cos(tangent - 2.4) * headLen, headY + Math.sin(tangent - 2.4) * headLen);
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.restore();
    }

    // ── Digit states ───────────────────────────────────────────────

    private drawDigitMarks(analysis: ClearAnalysis): void {
        // Over-crossed digits: a red square, no badges.
        for (const digit of analysis.redSquares) {
            const pos = this.grid.getDigitPosition(digit);
            if (pos) this.drawCellBorder(pos, COLORS.red);
        }

        // One badge per pass, on that pass's lane side.
        for (const b of analysis.badges) {
            const pos = this.grid.getDigitPosition(b.digit);
            if (!pos) continue;
            const off = this.laneOffset(b.dir);
            const quadrant: BadgeQuadrant = Math.abs(off.x) > Math.abs(off.y)
                ? (off.x > 0 ? 'E' : 'W')
                : (off.y > 0 ? 'S' : 'N');
            this.drawBadge(pos, quadrant, String(b.value), b.color, 'sm', true);
        }
    }

    // ── Path highlight (click a digit / the start) ─────────────────

    /**
     * Highlight, in translucent blue over the turn arrows, the path segment(s) leaving each
     * step for which `isSource` holds — running forward until the drive next reaches a digit
     * or the start (a landmark), with a flow arrow off the source and at every turn.
     */
    private drawPathHighlight(path: CartPath, isSource: (i: number) => boolean): void {
        const steps = path.steps;
        const n = steps.length;
        if (n === 0) return;

        // A segment ends at the next digit or at the seam — step 0, where the loop begins and
        // closes. Other visits to the start corner are driven through: the cart can pass back
        // through it, or bounce off the top edge there, with or without a turn on the corner.
        const isLandmark = (i: number) => this.grid.hasDigitAt(steps[i].pos) || i === 0;

        // The seam's two rim points: the cart departs into the grid heading east and returns
        // out heading west, so the highlight runs to/from the rim rather than stopping short.
        const h = this.config.cellSize / 2;
        const C0 = this.getCellCenter(TurnsGame.START);
        const eOff = this.laneOffset('E');
        const wOff = this.laneOffset('W');
        const departRim = { x: C0.x - h + eOff.x, y: C0.y + eOff.y };
        const returnRim = { x: C0.x - h + wOff.x, y: C0.y + wOff.y };

        this.ctx.lineWidth = this.config.cellSize * 0.13;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.strokeStyle = colorWithAlpha('blue', 0.55);

        for (let j = 0; j < n; j++) {
            if (!isSource(j)) continue;
            const seg: number[] = [];
            let k = (j + 1) % n;
            for (let guard = 0; guard < n && !isLandmark(k); guard++) { seg.push(k); k = (k + 1) % n; }
            if (seg.length === 0) continue;

            // One continuous path per segment (pieces share endpoints), so translucent joins
            // don't double up. Pieces connect because each step's exit is the next's entry.
            const g0 = this.stepGeom(path, seg[0]);
            this.ctx.beginPath();
            if (j === 0) {
                this.ctx.moveTo(departRim.x, departRim.y);   // depart from the start rim
                this.ctx.lineTo(g0.entry.x, g0.entry.y);
            } else {
                this.ctx.moveTo(g0.entry.x, g0.entry.y);
            }
            for (const i of seg) this.appendStepPath(path, i);
            if (k === 0) this.ctx.lineTo(returnRim.x, returnRim.y);   // return out to the start rim
            this.ctx.stroke();

            // A flow arrow at the start of the segment and at the start of each turn's curve.
            seg.forEach((i, idx) => {
                const g = this.stepGeom(path, i);
                if (idx === 0 || g.inDir !== g.outDir) this.drawFlowArrow(g.entry, g.inDir);
            });
        }
    }

    /** Entry/exit points and headings for a step's lane piece. */
    private stepGeom(path: CartPath, i: number) {
        const steps = path.steps;
        const n = steps.length;
        const inDir = steps[(i - 1 + n) % n].dir;
        const outDir = steps[i].dir;
        const inV = DIR_VEC[inDir];
        const outV = DIR_VEC[outDir];
        const h = this.config.cellSize / 2;
        const C = this.getCellCenter(steps[i].pos);
        const entry = { x: C.x - inV.x * h + this.laneOffset(inDir).x, y: C.y - inV.y * h + this.laneOffset(inDir).y };
        const exit = { x: C.x + outV.x * h + this.laneOffset(outDir).x, y: C.y + outV.y * h + this.laneOffset(outDir).y };
        return { inDir, outDir, inV, outV, entry, exit };
    }

    /** Append one non-landmark step's lane piece to the current path (no stroke). */
    private appendStepPath(path: CartPath, i: number): void {
        const g = this.stepGeom(path, i);
        const steps = path.steps;

        // A Vortex jump breaks the segment in two: run the lane into the gate the cart
        // enters, then restart at this gate's centre. Without the break the highlight
        // would draw one straight line clean across the grid between the two gates.
        if (steps[i].teleport) {
            const prev = steps[(i - 1 + steps.length) % steps.length];
            const entryGate = this.grid.move(prev.pos, prev.dir);
            if (isInside(entryGate)) {
                const ec = this.getCellCenter(entryGate);
                const eOff = this.laneOffset(prev.dir);
                this.ctx.lineTo(ec.x + eOff.x, ec.y + eOff.y);
            }
            const C = this.getCellCenter(steps[i].pos);
            const off = this.laneOffset(g.outDir);
            this.ctx.moveTo(C.x + off.x, C.y + off.y);
            this.ctx.lineTo(g.exit.x, g.exit.y);
            return;
        }

        if (g.outDir === oppositeDir(g.inDir)) {
            this.appendUTurn(g.entry, g.exit, g.inV, true);
        } else if (g.inDir === g.outDir) {
            this.ctx.lineTo(g.exit.x, g.exit.y);
        } else {
            const ctrl = tangentIntersect(g.entry, g.inV, g.exit, g.outV);
            this.ctx.quadraticCurveTo(ctrl.x, ctrl.y, g.exit.x, g.exit.y);
        }
    }

    /** A solid blue flow arrowhead centred at `point`, pointing `dir`. */
    private drawFlowArrow(point: Vec, dir: Direction): void {
        const v = DIR_VEC[dir];
        const perp = { x: -v.y, y: v.x };
        const size = this.config.cellSize * 0.15;

        this.ctx.fillStyle = colorWithAlpha('blue', 0.95);
        this.ctx.beginPath();
        this.ctx.moveTo(point.x + v.x * size, point.y + v.y * size);
        this.ctx.lineTo(point.x - v.x * size * 0.5 + perp.x * size * 0.7, point.y - v.y * size * 0.5 + perp.y * size * 0.7);
        this.ctx.lineTo(point.x - v.x * size * 0.5 - perp.x * size * 0.7, point.y - v.y * size * 0.5 - perp.y * size * 0.7);
        this.ctx.closePath();
        this.ctx.fill();
    }
}
